import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, Mail, Edit, Check, X, LogOut, Trash2, Zap, Loader } from 'lucide-react';
// Correct the import path for ConfirmationModal
import { ConfirmationModal } from '../shared/ConfirmationModal'; 

export const ProfileView = ({ currentUserData, onBack, onLogout, onUpdateProfile, onDeleteAccount }) => {
    const [isEditingName, setIsEditingName] = useState(false);
    const [newName, setNewName] = useState('');
    const [loading, setLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        if (currentUserData) {
            setNewName(currentUserData.name);
        }
    }, [currentUserData]);

    const handleUpdateName = async () => {
        if (newName.trim() === '' || newName.trim() === currentUserData.name) {
            setIsEditingName(false);
            return;
        }
        setLoading(true);
        try {
            await onUpdateProfile(newName.trim());
        } catch (error) {
            console.error("Failed to update name:", error);
            alert("Could not update your name. Please try again.");
        }
        setLoading(false);
        setIsEditingName(false);
    };
    
    const handleDelete = async () => {
        setShowDeleteConfirm(false);
        try {
            await onDeleteAccount();
            // On success, the AuthContext will handle logout and state clearing.
        } catch (error) {
             // Error is alerted from AuthContext
        }
    };

    if (!currentUserData) {
        return <div className="p-4 text-center">Loading profile...</div>;
    }

    return (
        <div className="max-w-xl mx-auto p-4 sm:p-6">
            <ConfirmationModal
                show={showDeleteConfirm}
                title="Delete Account?"
                message="Are you sure? This action is permanent and cannot be undone. All of your match data will be lost."
                onConfirm={handleDelete}
                onClose={() => setShowDeleteConfirm(false)}
                confirmText="Delete Account"
                isDestructive={true}
            />
            <div className="flex items-center mb-6">
                <button onClick={onBack} className="p-2 rounded-full hover:bg-slate-700 -ml-2">
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <h1 className="text-2xl sm:text-3xl font-bold ml-4">Profile & Settings</h1>
            </div>

            <div className="space-y-6">
                {/* Profile Details Card */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                    <h2 className="text-lg font-bold mb-4">Account Information</h2>
                    <div className="space-y-4">
                        {/* Name Field */}
                        <div>
                            <label className="text-sm font-medium text-slate-400 flex items-center gap-2 mb-2"><User size={16} /> Name</label>
                            {isEditingName ? (
                                <div className="flex items-center gap-2">
                                    <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-400" />
                                    <button onClick={handleUpdateName} disabled={loading} className="p-2 bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">
                                        {loading ? <Loader size={20} className="animate-spin" /> : <Check size={20} />}
                                    </button>
                                    <button onClick={() => setIsEditingName(false)} disabled={loading} className="p-2 bg-slate-600 rounded-lg hover:bg-slate-500 disabled:opacity-50">
                                        <X size={20} />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between">
                                    <p className="text-lg">{currentUserData.name}</p>
                                    <button onClick={() => setIsEditingName(true)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-full">
                                        <Edit size={18} />
                                    </button>
                                </div>
                            )}
                        </div>
                        {/* Email Field */}
                        <div>
                           <label className="text-sm font-medium text-slate-400 flex items-center gap-2 mb-2"><Mail size={16} /> Email</label>
                           <p className="text-lg text-slate-300">{currentUserData.email}</p>
                        </div>
                         {/* Plan Field */}
                        <div>
                           <label className="text-sm font-medium text-slate-400 flex items-center gap-2 mb-2"><Zap size={16} /> Plan</label>
                           <p className={`text-lg font-bold capitalize ${currentUserData.plan === 'pro' ? 'text-yellow-400' : 'text-slate-300'}`}>{currentUserData.plan || 'Free'}</p>
                        </div>
                    </div>
                </div>

                {/* Actions Card */}
                 <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                    <h2 className="text-lg font-bold mb-4">Actions</h2>
                    <div className="space-y-3">
                         <button onClick={onLogout} className="w-full flex items-center justify-center gap-3 bg-slate-600 text-white py-2.5 rounded-lg font-medium hover:bg-slate-700 transition-colors">
                            <LogOut size={18} /> Log Out
                        </button>
                        <button onClick={() => setShowDeleteConfirm(true)} className="w-full flex items-center justify-center gap-3 bg-red-800/80 text-white py-2.5 rounded-lg font-medium hover:bg-red-700/80 transition-colors">
                           <Trash2 size={18} /> Delete Account
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};